import React, { useEffect, useRef, useState } from 'react'
import { KeyboardAvoidingView, Platform, ScrollView, StyleSheet, Text, View } from 'react-native'

import { NavigationProp, RouteProp, useNavigation, useRoute } from '@react-navigation/native'

import {
  CategoryFourFailureForm,
  CategoryOneFailureForm,
  CategoryThreeFailureForm,
  CategoryTwoFailureForm,
  Tag,
  WorkSummary,
} from '../components'

import { useValuesFinalForm } from '../hooks/useValuesFinalForm'
import { FloatingBottomButtons } from '../../../components/FloatingBottom/FloatingBottom'
import { TemplatePage } from '../../../components/TemplatePage'
import { useForm } from '../../../hooks/useForm'
import { useKeyboardVisibility } from '../../../hooks/useKeyboardVisibility'
import { globalStyle } from '../../../theme/global'
import {
  Detail,
  FailureValues,
  NetWashingDetail,
  ReqControlService,
  ResControlService,
} from '../../../types'
import { ControlServiceRecord } from '../../../context/Alerts/types'
import { usePost } from '../../../hooks/usePost'
import { paths } from '../../../api/pathsApi'
import { useTranslation } from 'react-i18next'
import { AlertMessage } from '../../../components/AlertMessage'
import { useSendNotifications } from '../../../hooks/useSendNotifications'
import { Alert401 } from '../../../components/AlertMessage/Alert401'
import { useContextApp } from '../../../hooks/useContextApp'
import {
  CenterCategoryOneType,
  CenterInfo,
  ControlServicesRoutes,
  RootParams,
  ShiftType,
} from '../../../navigator/types'
import { corporateColor, textColor } from '../../../theme/colors'
import { SwitchComponent } from '../../../components/SwitchComponent'
import { DOBRA } from '../../../theme/fonts'
import { RadioButton } from '../../../components/RadioButton'
import { TextInputComponent } from '../../../components/TextInputComponent'

interface JobItem {
  jobName: string
  choreName: string
  jobId?: number
  choreId?: number
  centerId?: number
}

const CATEGORY_FORM_MAP: {
  [key: number]: (props: {
    initialValues?: FailureValues
    getValues: (value: FailureValues) => void
  }) => React.JSX.Element
} = {
  1: CategoryOneFailureForm,
  2: CategoryTwoFailureForm,
  3: CategoryThreeFailureForm,
  4: CategoryFourFailureForm,
}

const OPTIONS = ['CECO', 'Código de apertura']
interface Navigation extends NavigationProp<RootParams, 'ControlServicesStack'> {}

export const FinalForm = () => {
  const { t } = useTranslation()
  const {
    user: { areaSelected },
    handleLogOut,
    seaWaterContext,
    controlServicesContext,
    handleSaveRecords,
    deleteControlSevice,
    getTotalPendingRecords,
  } = useContextApp()

  useSendNotifications({ pendingRecords: getTotalPendingRecords(areaSelected!) })

  const { navigate, goBack } = useNavigation<Navigation>()
  const route = useRoute<RouteProp<ControlServicesRoutes, 'FinalForm'>>()
  const [showAlert, setShowAlert] = useState(false)
  const [showSummary, setShowSummary] = useState(false)

  const [showErrorAlert, setShowErrorAlert] = useState({
    show: false,
    message: '',
  })
  const [showDuplicatedAlert, setShowDuplicatedAlert] = useState({
    show: false,
    message: '',
    navigateOnConfirm: false,
  })

  const {
    categoryFailure,
    centerId,
    choreId,
    date,
    categoryId,
    serviceId,
    boardingName,
    formKey,
    failure,
    centersCategoryOne,
    currentCenter,
    observation,
  } = route.params

  const { values: paramValues, onChangeValues: handleSetParamValues } = useForm({
    centersCategoryOne: route.params.centersCategoryOne ?? ({} as CenterCategoryOneType),
  })

  const [selectedOption, setSelectedOption] = useState<string>(
    categoryId === 1
      ? centersCategoryOne[currentCenter]
        ? centersCategoryOne[currentCenter].ceco
        : ''
      : 'CECO',
  )

  const changeCECO = (value: string) => {
    if (categoryId === 1) {
      handleSetParamValues({
        name: 'centersCategoryOne',
        value: {
          ...paramValues.centersCategoryOne,
          [currentCenter]: {
            ...paramValues.centersCategoryOne[currentCenter],
            ceco: value,
          },
        } as CenterCategoryOneType,
      })
    } else {
      setSelectedOption(value)
    }
  }

  const { actions, handleChangeAction, handleChangeObservation, MAX_CHARACTERS, MIN_CHARACTERS } =
    useValuesFinalForm({
      declareFailure: failure
        ? Object.values(failure).some(
            (item) =>
              (typeof item === 'number' && item > 0) ||
              (typeof item === 'boolean' && item === true),
          )
        : false,
      observation: observation ?? '',
    })

  const changeObservation = (value: string) => {
    if (value.length > MAX_CHARACTERS) return
    if (categoryId === 1) {
      handleSetParamValues({
        name: 'centersCategoryOne',
        value: {
          ...paramValues.centersCategoryOne,
          [currentCenter]: {
            ...paramValues.centersCategoryOne[currentCenter],
            observation: value,
          },
        } as CenterCategoryOneType,
      })
    } else {
      handleChangeObservation(value)
    }
  }

  const pendingPayloadRef = useRef<ReqControlService[]>([])

  const detailRef = useRef<Detail[] | []>([])
  if ('detail' in route.params) {
    const { detail } = route.params
    detailRef.current = detail ?? []
  }
  const washingRef = useRef<Omit<NetWashingDetail, 'washingTypeName'>[]>([])
  if ('netWashingDetail' in route.params) {
    const { netWashingDetail } = route.params
    washingRef.current = netWashingDetail ?? []
  }
  const { isKeyboardVisible } = useKeyboardVisibility()
  const [error401, setError401] = useState<{ code: number | null; msg: string }>({
    code: null,
    msg: '',
  })

  const { handleSendData, isLoading } = usePost<ReqControlService[], ResControlService>({
    url: paths().CONTROL_SERVICES.SAVE_DATA,
    onSuccess(data, body) {
      if ('success' in data && data.success && body[0] && body[0].confirm) {
        if (formKey) {
          deleteControlSevice([formKey])
        }
        navigate('SuccessScreen', {
          goNewForm: true,
        })
        return
      }
      if ('success' in data && data.success && body[0] && !body[0].confirm) {
        sendData({ confirm: true })
        return
      }
      if ('warning' in data && body[0] && !body[0].confirm) {
        const { warning } = data
        setShowDuplicatedAlert({
          show: true,
          navigateOnConfirm: false,
          message: warning
            .map(
              (w) =>
                `${t('Embarcación')}: ${w.boarding} | ${t('Centro')}: ${w.center} | ${t(
                  'Fecha',
                )}: ${w.date}`,
            )
            .join('\n'),
        })
        return
      }
      if ('warning' in data && body[0] && body[0].confirm) {
        if (categoryId === 1) {
          const { warning } = data
          setShowDuplicatedAlert({
            show: true,
            message: warning
              .map(
                (w) =>
                  `${t('Centro')}: ${w.center} | ${t('Fecha')}: ${w.date} | ${t('Embarcación')}: ${
                    w.boarding
                  }`,
              )
              .join('\n'),
            navigateOnConfirm: true,
          })
          return
        }
        if (formKey) {
          deleteControlSevice([formKey])
        }
        navigate('SuccessScreen')
        return
      }
    },

    onError(error) {
      if (error.code === 401) {
        handleSaveRecord()
        seaWaterContext.handleResetData()
        controlServicesContext.handleResetData()
        setError401(error)
        return
      }
      setShowErrorAlert({ show: true, message: error.msg })
    },
  })
  const { values, onChangeValues: handleSetValues } = useForm<FailureValues>({
    diver: failure?.diver ?? null,
    ship: failure?.ship ?? null,
    dotation: failure?.dotation ?? null,
    noRelief: failure?.noRelief ?? null,
    hydraulic: failure?.hydraulic ?? null,
    work: failure?.work ?? null,
  })
  const FailureForm = CATEGORY_FORM_MAP[categoryFailure]

  const handleFailureValues = (values: FailureValues) => {
    for (const key in values) {
      handleSetValues({
        name: key as keyof FailureValues,
        value: values[key as keyof FailureValues],
      })
    }
  }

  useEffect(() => {
    if (categoryId === 1) {
      const failureData = !actions.declareFailure
        ? null
        : Object.values(values).every((val) => val === null || !val)
        ? null
        : values

      handleSetParamValues({
        name: 'centersCategoryOne',
        value: {
          ...paramValues.centersCategoryOne,
          [currentCenter]: {
            ...paramValues.centersCategoryOne[currentCenter],
            failure: failureData,
          },
        } as CenterCategoryOneType,
      })
    }
  }, [values, actions.declareFailure])

  const sendData = ({ confirm }: { confirm: boolean }) => {
    // Si es un reenvío con confirm:true y ya existe el payload cacheado, reutilizarlo directamente
    // para evitar el problema de stale closure con useMutation de react-query
    if (confirm && categoryId === 1 && pendingPayloadRef.current.length > 0) {
      const objToSend = pendingPayloadRef.current.map((item) => ({ ...item, confirm: true }))
      handleSendData(objToSend as ReqControlService[])
      return
    }

    // Validar AM-PM para múltiples centros
    if (categoryId === 1) {
      const shiftValidation = validateShiftCompletion()

      if (!shiftValidation.isComplete) {
        // Si no hay Jornada Completa, requiere al menos 1 AM y 1 PM
        setShowErrorAlert({
          show: true,
          message:
            'Debe completar al menos un centro con jornada AM y otro con jornada PM. O seleccione "Jornada Completa" en algún centro.',
        })
        return
      }
    }

    const failureData = !actions.declareFailure
      ? null
      : Object.values(values).every((val) => val === null || !val)
      ? null
      : values

    let objToSend = [
      {
        boardingId: serviceId,
        ceco: selectedOption === 'CECO',
        centerId,
        choreId,
        confirm,
        date,
        detail: detailRef.current,
        failure: failureData,
        netWashingDetail: washingRef.current,
        observation: actions.observation,
        percentage: route.params.percentage,
        valid: false,
        shift: route.params.shiftState,
      },
    ]

    if (categoryId === 1) {
      objToSend = []

      // Iterar sobre cada centro
      for (const centerIdItem in paramValues.centersCategoryOne) {
        const paramInfo = paramValues.centersCategoryOne[Number(centerIdItem)] as CenterInfo
        const centerId = Number(centerIdItem)
        const centerJobList = paramInfo?.jobList ?? {}

        // Calcular shifts dinámicamente desde jobList para evitar duplicados
        const shiftsToUse = Object.keys(centerJobList).filter(
          (shift) => Object.keys(centerJobList[shift] ?? {}).length > 0,
        )

        // Si no hay shifts con trabajos, no enviar nada para este centro
        if (shiftsToUse.length === 0) {
          continue
        }

        // Iterar por cada shift y crear registros
        for (const shiftType of shiftsToUse) {
          const shiftJobList = (centerJobList[shiftType] ?? {}) as { [key: string]: JobItem }

          // Convertir a array una sola vez
          const jobListArray = Object.values(shiftJobList)

          for (const jobItem of jobListArray) {
            objToSend.push({
              boardingId: serviceId,
              ceco: paramInfo.ceco === 'CECO',
              centerId,
              choreId: jobItem.choreId ?? 0,
              confirm,
              date,
              detail: detailRef.current,
              failure: failureData,
              netWashingDetail: washingRef.current,
              observation: paramInfo.observation,
              percentage: 0,
              shift: shiftType as ShiftType,
              valid: false,
            })
          }
        }
      }
    }

    if (categoryId === 1 && !confirm) {
      pendingPayloadRef.current = objToSend as ReqControlService[]
    }
    handleSendData(objToSend as ReqControlService[])
  }

  const handleDisabledButton = (): boolean => {
    if (actions.declareFailure) {
      return Object.values(values).every((fail) => fail === null || !fail) || commonDisabledButton()
    }

    return commonDisabledButton()
  }

  const commonDisabledButton = (): boolean => {
    if (categoryId === 1) {
      return (
        (paramValues.centersCategoryOne[currentCenter]?.observation.length ?? 0) < MIN_CHARACTERS
      )
    } else {
      return (actions.observation?.length ?? 0) < MIN_CHARACTERS
    }
  }

  const handleSaveRecord = () => {
    const milliseconds = Date.now()
    const currentDate = new Date(milliseconds)
    const failureData = !actions.declareFailure
      ? null
      : Object.values(values).every((val) => val === null || !val)
      ? null
      : values

    const objToSend = [
      {
        date,
        centerId,
        choreId,
        boardingId: serviceId,
        observation: actions.observation,
        detail: detailRef.current,
        failure: failureData,
        confirm: true,
        netWashingDetail: washingRef.current,
        ceco: selectedOption === 'CECO',
        percentage: route.params.percentage,
        valid: false,
      },
    ]

    handleSaveRecords<ControlServiceRecord>({
      area: 'seaWater',
      key: formKey,
      record: {
        ...route.params,
        ...objToSend,
        dateTime: currentDate.toUTCString(),
        boardingId: serviceId,
        observation: actions.observation!,
        detail: detailRef.current,
        ceco: selectedOption === 'CECO',
        confirm: false,
        valid: false,
        serviceName: boardingName,
        netWashingDetail: [],
      },
      typeAlert: 'controlServiceRecords',
    })
  }

  const validateAlertMinCaracter = (): boolean => {
    if (categoryId !== 1) {
      return (
        (actions.observation?.length ?? 0) < MIN_CHARACTERS &&
        (actions.observation?.length ?? 0) !== 0
      )
    }
    const lengthCategoryOne = paramValues.centersCategoryOne[currentCenter]?.observation.length ?? 0
    return lengthCategoryOne < MIN_CHARACTERS && lengthCategoryOne !== 0
  }

  const validateShiftCompletion = () => {
    let hasAM = false
    let hasPM = false
    let hasCompleteShift = false

    // Validar globalmente revisando los jobList de todos los centros
    Object.values(paramValues.centersCategoryOne).forEach((center) => {
      const centerInfo = center as CenterInfo
      const jobList = centerInfo?.jobList ?? {}

      // Revisar cada shift en el jobList del centro
      Object.entries(jobList).forEach(([shift, jobs]) => {
        if (Object.keys(jobs).length > 0) {
          if (shift === 'AM') hasAM = true
          if (shift === 'PM') hasPM = true
          if (shift === 'Jornada Completa') hasCompleteShift = true
        }
      })
    })

    return {
      hasAM,
      hasPM,
      hasCompleteShift,
      isComplete: hasCompleteShift || (hasAM && hasPM),
    }
  }

  if (showSummary && categoryId === 1) {
    const shiftValidation = validateShiftCompletion()
    const totalCenters = Object.keys(paramValues.centersCategoryOne).length
    const isSingleCenter = totalCenters === 1

    return (
      <TemplatePage
        styleContent={{ padding: 0, marginBottom: 0 }}
        header
        title={'CONTROL SERVICES'}
      >
        <View style={globalStyle.viewContainer}>
          {!isSingleCenter && !shiftValidation.isComplete && (
            <AlertMessage title='Validación de Jornada' action={() => {}}>
              <Text style={{ textAlign: 'center', marginBottom: 8, color: textColor }}>
                Jornada Incompleta
              </Text>
              <Text style={{ textAlign: 'center', fontSize: 12, color: textColor }}>
                {shiftValidation.hasCompleteShift
                  ? 'Jornada Completa registrada'
                  : `Tiene AM: ${shiftValidation.hasAM ? '✓' : '✗'} | Tiene PM: ${
                      shiftValidation.hasPM ? '✓' : '✗'
                    }`}
              </Text>
            </AlertMessage>
          )}

          {!isSingleCenter && shiftValidation.isComplete && (
            <AlertMessage title='Validación de Jornada' action={() => {}}>
              <Text style={{ textAlign: 'center', color: '#4CAF50', fontWeight: 'bold' }}>
                ✓ Jornada Completa
              </Text>
              <Text style={{ textAlign: 'center', fontSize: 12, marginTop: 4, color: textColor }}>
                {shiftValidation.hasCompleteShift
                  ? 'Tiene Jornada Completa'
                  : 'Tiene registrados AM y PM'}
              </Text>
            </AlertMessage>
          )}

          <WorkSummary
            centersCategoryOne={paramValues.centersCategoryOne}
            boardingName={boardingName}
            date={date}
          />
        </View>
        {!isKeyboardVisible && (
          <FloatingBottomButtons
            isLoading={isLoading}
            disabled={false}
            hideSave={true}
            sendText='ENVIAR'
            onPressSend={() => sendData({ confirm: false })}
            onPressSave={() => {}}
            onPressBack={() => setShowSummary(false)}
          />
        )}
        {showDuplicatedAlert.show && (
          <AlertMessage
            title={t('Duplicado')}
            action={() => {
              if (showDuplicatedAlert.navigateOnConfirm) {
                if (formKey) deleteControlSevice([formKey])
                setShowDuplicatedAlert({ show: false, message: '', navigateOnConfirm: false })
                navigate('SuccessScreen')
              } else {
                sendData({ confirm: true })
                setShowDuplicatedAlert({ show: false, message: '', navigateOnConfirm: false })
              }
            }}
            successLabel='Enviar'
            cancelButton
            actionCancelButton={() =>
              setShowDuplicatedAlert({ show: false, message: '', navigateOnConfirm: false })
            }
          >
            <Text style={{ textAlign: 'center', color: textColor }}>
              {showDuplicatedAlert.message}
            </Text>
          </AlertMessage>
        )}
      </TemplatePage>
    )
  }

  return (
    <TemplatePage styleContent={{ padding: 0, marginBottom: 0 }} header title={'CONTROL SERVICES'}>
      <View style={globalStyle.viewContainer}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={100}
          style={{ flex: 1 }}
        >
          <ScrollView style={{ flex: 1, padding: 24, gap: 8 }}>
            <Text style={styles.title}>{t('Declaración de fallas')}</Text>
            {categoryId === 1 && <Tag label='Embarcación' name={boardingName} variant='outlined' />}
            {categoryId !== 1 && (
              <View style={{ gap: 8 }}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Tag label='Centro' name={route.params.centerName} variant='outlined' />
                  <Tag
                    label='Fecha'
                    name={date.split('-').reverse().join('/')}
                    variant='outlined'
                  />
                </View>
                <Tag label='Embarcación' name={boardingName} variant='outlined' />
              </View>
            )}

            <View style={{ marginVertical: 16 }}>
              <View style={styles.declareFailureContainer}>
                <Text style={styles.declareFailureLabel}>Declarar falla</Text>
                <SwitchComponent
                  onPress={() => handleChangeAction('declareFailure')}
                  isActive={actions.declareFailure!}
                />
              </View>
              <Text style={styles.decalreFailureDesc}>
                Seleccione en caso de haber falla en los centros seleccionados
              </Text>
            </View>

            {actions.declareFailure && FailureForm && (
              <FailureForm getValues={handleFailureValues} initialValues={values} />
            )}

            {categoryId !== 1 && (
              <>
                <RadioButton
                  onSelect={changeCECO}
                  options={OPTIONS}
                  selected={
                    categoryId !== 1
                      ? selectedOption
                      : paramValues.centersCategoryOne[currentCenter]
                      ? paramValues.centersCategoryOne[currentCenter].ceco
                      : 'CECO'
                  }
                />
                <View style={{ marginTop: 8, marginBottom: 150 }}>
                  <TextInputComponent
                    label='Observaciones'
                    multiline
                    value={
                      categoryId !== 1
                        ? actions.observation!
                        : paramValues.centersCategoryOne[currentCenter]
                        ? paramValues.centersCategoryOne[currentCenter].observation
                        : ''
                    }
                    customStyles={{
                      input: validateAlertMinCaracter()
                        ? { height: 200, textAlignVertical: 'top', borderColor: 'red' }
                        : { height: 200, textAlignVertical: 'top' },
                    }}
                    onChange={changeObservation}
                  />
                  <View style={styles.containerText}>
                    {validateAlertMinCaracter() && (
                      <Text style={styles.minCaracter}>Mínimo 15 caracteres</Text>
                    )}
                    <Text style={styles.characterCount}>{`${
                      categoryId !== 1
                        ? actions.observation?.length
                        : paramValues.centersCategoryOne[currentCenter]?.observation.length
                    }/${MAX_CHARACTERS}`}</Text>
                  </View>
                </View>
              </>
            )}
          </ScrollView>
        </KeyboardAvoidingView>
      </View>
      {!isKeyboardVisible && (
        <FloatingBottomButtons
          isLoading={isLoading}
          disabled={handleDisabledButton()}
          onPressSave={() => {
            if (categoryId === 1) {
              setShowSummary(true)
            } else {
              handleSaveRecord()
              setShowAlert(true)
            }
          }}
          hideSave={false}
          sendText={categoryId === 1 ? 'CONTINUAR' : 'ENVIAR'}
          onPressSend={() => {
            if (categoryId === 1) {
              setShowSummary(true)
            } else {
              sendData({ confirm: false })
            }
          }}
          onPressBack={goBack}
        />
      )}

      <Alert401 isRender={error401.code === 401} action={handleLogOut} />

      {showAlert && (
        <AlertMessage
          title={t('Guardado')}
          action={() => {
            setShowAlert(false)
          }}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>
            {t('El registro se guardó con éxito')}
          </Text>
        </AlertMessage>
      )}

      {showDuplicatedAlert.show && (
        <AlertMessage
          title={t('Duplicado')}
          action={() => {
            if (showDuplicatedAlert.navigateOnConfirm) {
              if (formKey) deleteControlSevice([formKey])
              setShowDuplicatedAlert({ show: false, message: '', navigateOnConfirm: false })
              navigate('SuccessScreen')
            } else {
              sendData({ confirm: true })
              setShowDuplicatedAlert({ show: false, message: '', navigateOnConfirm: false })
            }
          }}
          successLabel='Enviar'
          cancelButton
          actionCancelButton={() =>
            setShowDuplicatedAlert({ show: false, message: '', navigateOnConfirm: false })
          }
        >
          <Text style={{ textAlign: 'center' }}>{showDuplicatedAlert.message}</Text>
        </AlertMessage>
      )}
      {showErrorAlert.show && (
        <AlertMessage
          title={t('Error')}
          action={() => setShowErrorAlert({ show: false, message: '' })}
        >
          <Text style={{ textAlign: 'center', color: textColor }}>{showErrorAlert.message}</Text>
        </AlertMessage>
      )}
    </TemplatePage>
  )
}

const styles = StyleSheet.create({
  title: {
    textAlign: 'center',
    fontWeight: 'bold',
    color: corporateColor,
    marginBottom: 16,
  },
  declareFailureContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  declareFailureLabel: {
    fontFamily: DOBRA,
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 20,
    color: textColor,
  },
  decalreFailureDesc: {
    fontFamily: DOBRA,
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: textColor,
  },
  characterCount: {
    marginTop: 5,
    color: 'gray',
  },
  minCaracter: {
    marginTop: 5,
    color: 'red',
  },

  containerText: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
})
